import type { useSnackBar } from '@usealloy/component-library';
import type { Action, PublicId, WorkflowProps } from 'flow/store/engine/types';
import type { AppDispatch } from '~/store';
import getRemoteCurrentAndNextLocalSnippets from './getRemoteCurrentAndNextLocalSnippets';
import { setSnippetCollapsed, setEditorUiMode, resetEditorUiMode } from 'flow/store/redux/slice';
import { upgradeLinkedSnippetVersionThunk } from 'flow/store/redux/thunks/snippets';
import type { API } from '~/helpers/api-new/modules';
import { snippetMode } from 'flow/store/redux/types';
import { getSnippetActionsWithNonResolvableCollisions } from 'flow/service/snippets/collisions';
import { isChildOfSnippetAction } from 'flow/service/actions/snippets';
import { getActionName } from 'flow/service/actions';
import { ActionType } from 'flow/store/engine/constants';

export interface UpgradeLinkedSnippetVersionArgs {
  addSnackBar: ReturnType<typeof useSnackBar>;
  currentVersion: number;
  dispatch: AppDispatch;
  libraryApi: API['library'];
  localSnippetActionPublicId: PublicId;
  nextVersion: number;
  snippetToken: string;
  typhonDomain: string;
  workflowActions: Record<Action['publicId'], Action>;
  workflowProps: WorkflowProps;
}

/**
 * Fetches current/next remote snippet versions, dispatches the upgrade thunk,
 * and shows an error snackbar on failure. Used by node dropdown and UpdateSnippetDropdown.
 */
export default async function upgradeLinkedSnippetVersion({
  addSnackBar,
  currentVersion,
  dispatch,
  libraryApi,
  localSnippetActionPublicId,
  nextVersion,
  snippetToken,
  typhonDomain,
  workflowActions,
  workflowProps,
}: UpgradeLinkedSnippetVersionArgs): Promise<void> {
  try {
    // Enter paste mode here so spinner shows while fetching
    dispatch(setEditorUiMode(snippetMode.pastePending));

    const {
      remoteCurrentSnippet,
      remoteNextSnippet,
    } = await getRemoteCurrentAndNextLocalSnippets({
      currentVersion,
      libraryApi,
      nextVersion,
      snippetToken,
      typhonDomain,
      workflowProps,
    });

    const workflowActionsExcludingSnippet: Record<string, Action> = {};
    for (const [id, action] of Object.entries(workflowActions)) {
      const belongsToSnippet = isChildOfSnippetAction({
        action,
        snippetParentActionPublicId: localSnippetActionPublicId,
      });
      if (!belongsToSnippet) {
        workflowActionsExcludingSnippet[id] = action;
      }
    }

    const collisions = getSnippetActionsWithNonResolvableCollisions({
      snippetActions: remoteNextSnippet.data.actions,
      workflowActions: workflowActionsExcludingSnippet,
    });

    if (collisions.length > 0) {
      dispatch(resetEditorUiMode());
      collisions.forEach(collision => {
        const name = getActionName(collision);
        const title = collision.type === ActionType.INPUT
          ? `"${name}" already exists with a different data type and/or jq. Rename to upgrade Snippet.`
          : `"${name}" already exists. Rename to upgrade Snippet.`;
        addSnackBar({
          title,
          type: 'danger',
          duration: 5_000,
        });
      });
      return;
    }

    await dispatch(upgradeLinkedSnippetVersionThunk({
      currentRemoteSnippet: remoteCurrentSnippet,
      localSnippetActionPublicId,
      nextRemoteSnippet: remoteNextSnippet,
    }));

    dispatch(setSnippetCollapsed({ isCollapsed: false, snippetId: localSnippetActionPublicId }));
  } catch {
    dispatch(resetEditorUiMode());
    addSnackBar({
      title: 'Error updating snippet version',
      type: 'danger',
      duration: 5_000,
    });
  }
}
